/**
 * Context Intelligence Admin Page
 * =================================
 * 
 * Allows users to view and manage context events that affect business performance.
 * This is the "human-in-the-loop" interface for teaching PIQ about external factors.
 * 
 * Tabs:
 * - Active Now: Currently active events
 * - Upcoming: Events starting in the next 30 days
 * - Calendar: Visual calendar view
 * - Templates: Pre-built event templates
 * - Learning: Confidence trends and outcomes
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Calendar, 
  Plus, 
  Clock, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  BookOpen,
  Brain,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  X,
  Edit2,
  Trash2,
  Sparkles,
} from 'lucide-react';
import ContextEventForm from '../../components/admin/ContextEventForm';
import ContextCalendar from '../../components/admin/ContextCalendar';
import TemplateLibrary from '../../components/admin/TemplateLibrary';
import ContextLearning from '../../components/admin/ContextLearning';

// =============================================================================
// TYPES
// =============================================================================

interface ContextEvent {
  id: string;
  event_type: string;
  event_name: string;
  description: string | null;
  start_date: string;
  end_date: string | null;
  is_recurring: boolean;
  recurrence_rule: string | null;
  expected_impact_direction: 'up' | 'down' | 'mixed' | null;
  expected_impact_pct_low: number | null;
  expected_impact_pct_high: number | null;
  affected_channels: string[];
  affected_platforms: string[];
  source: string;
  confidence: number;
  tags: string[];
  notes: string | null;
  days_active?: number;
  days_remaining?: number | null;
  days_until_start?: number;
}

interface ActiveEventsResponse {
  success: boolean;
  events: ContextEvent[];
  summary: {
    total: number;
    expecting_up: number;
    expecting_down: number;
    expecting_mixed: number;
    high_confidence: number;
  };
  as_of: string;
}

interface UpcomingEventsResponse {
  success: boolean;
  events: ContextEvent[];
  count: number;
  look_ahead_days: number;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

async function fetchActiveEvents(): Promise<ActiveEventsResponse> {
  const res = await fetch('/api/admin/context-events/active');
  if (!res.ok) throw new Error('Failed to fetch active events');
  return res.json();
}

async function fetchUpcomingEvents(days: number = 30): Promise<UpcomingEventsResponse> {
  const res = await fetch(`/api/admin/context-events/upcoming?days=${days}`);
  if (!res.ok) throw new Error('Failed to fetch upcoming events');
  return res.json();
}

async function fetchAllEvents(): Promise<{ events: ContextEvent[] }> {
  const res = await fetch('/api/admin/context-events');
  if (!res.ok) throw new Error('Failed to fetch events');
  return res.json();
}

async function deleteEvent(eventId: string): Promise<void> {
  const res = await fetch(`/api/admin/context-events/${eventId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete event');
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const ImpactBadge: React.FC<{ direction: string | null; low?: number | null; high?: number | null }> = ({ 
  direction, low, high 
}) => {
  if (!direction) return null;
  
  const colors = {
    up: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    down: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    mixed: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };
  
  const icons = {
    up: <TrendingUp className="w-3 h-3" />,
    down: <TrendingDown className="w-3 h-3" />,
    mixed: <Minus className="w-3 h-3" />,
  };
  
  const range = low !== null && high !== null 
    ? `${low > 0 ? '+' : ''}${low}% to ${high > 0 ? '+' : ''}${high}%`
    : direction;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${colors[direction as keyof typeof colors] || 'bg-gray-500/20 text-gray-400'}`}>
      {icons[direction as keyof typeof icons]}
      {range}
    </span>
  );
};

const EventTypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const colors: Record<string, string> = {
    internal_promo: 'bg-purple-500/20 text-purple-400',
    retailer_promo: 'bg-blue-500/20 text-blue-400',
    competitor_activity: 'bg-orange-500/20 text-orange-400',
    industry_event: 'bg-cyan-500/20 text-cyan-400',
    seasonality: 'bg-green-500/20 text-green-400',
    custom: 'bg-gray-500/20 text-gray-400',
  };
  
  const labels: Record<string, string> = {
    internal_promo: 'Internal Promo',
    retailer_promo: 'Retailer Promo',
    competitor_activity: 'Competitor',
    industry_event: 'Industry Event',
    seasonality: 'Seasonal',
    custom: 'Custom',
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${colors[type] || colors.custom}`}>
      {labels[type] || type}
    </span>
  );
};

const ConfidenceMeter: React.FC<{ confidence: number }> = ({ confidence }) => {
  const pct = Math.round(confidence * 100);
  const color = confidence >= 0.7 ? 'bg-emerald-500' : confidence >= 0.4 ? 'bg-amber-500' : 'bg-rose-500';
  
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-400">{pct}%</span>
    </div>
  );
};

const EventCard: React.FC<{ 
  event: ContextEvent; 
  onEdit: (event: ContextEvent) => void;
  onDelete: (eventId: string) => void;
}> = ({ event, onEdit, onDelete }) => {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00Z').toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      timeZone: 'UTC'
    });
  };
  
  return (
    <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 hover:border-gray-600/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <EventTypeBadge type={event.event_type} />
            <ImpactBadge 
              direction={event.expected_impact_direction} 
              low={event.expected_impact_pct_low}
              high={event.expected_impact_pct_high}
            />
          </div>
          
          <h3 className="font-medium text-white truncate">{event.event_name}</h3>
          
          {event.description && (
            <p className="text-sm text-gray-400 mt-1 line-clamp-2">{event.description}</p>
          )}
          
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(event.start_date)}
              {event.end_date && ` - ${formatDate(event.end_date)}`}
            </span>
            
            {event.days_active !== undefined && event.days_active >= 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Day {event.days_active + 1}
                {event.days_remaining !== null && ` (${event.days_remaining} left)`}
              </span>
            )}
            
            {event.days_until_start !== undefined && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Starts in {event.days_until_start} days
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-4 mt-2">
            <ConfidenceMeter confidence={event.confidence} />
            
            {event.affected_platforms.length > 0 && (
              <span className="text-xs text-gray-500">
                {event.affected_platforms.join(', ')}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button 
            onClick={() => onEdit(event)}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button 
            onClick={() => onDelete(event.id)}
            className="p-1.5 text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 rounded transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

type TabType = 'active' | 'upcoming' | 'calendar' | 'templates' | 'learning';

const ContextIntelligence: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ContextEvent | null>(null);
  
  const queryClient = useQueryClient();
  
  const { data: activeData, isLoading: activeLoading } = useQuery({
    queryKey: ['context-events', 'active'],
    queryFn: fetchActiveEvents,
    refetchInterval: 60000, // Refresh every minute
  });
  
  const { data: upcomingData, isLoading: upcomingLoading } = useQuery({
    queryKey: ['context-events', 'upcoming'],
    queryFn: () => fetchUpcomingEvents(30),
  });
  
  const { data: allEventsData } = useQuery({
    queryKey: ['context-events', 'all'],
    queryFn: fetchAllEvents,
  });
  
  const deleteMutation = useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['context-events'] });
    },
  });
  
  const handleEdit = (event: ContextEvent) => {
    setEditingEvent(event);
    setShowForm(true);
  };
  
  const handleDelete = (eventId: string) => {
    if (confirm('Are you sure you want to delete this event?')) {
      deleteMutation.mutate(eventId);
    }
  };
  
  const handleFormClose = () => {
    setShowForm(false);
    setEditingEvent(null);
    queryClient.invalidateQueries({ queryKey: ['context-events'] });
  };
  
  const tabs: { id: TabType; label: string; icon: React.ReactNode; count?: number }[] = [
    { 
      id: 'active', 
      label: 'Active Now', 
      icon: <Clock className="w-4 h-4" />,
      count: activeData?.summary.total 
    },
    { 
      id: 'upcoming', 
      label: 'Upcoming', 
      icon: <ChevronRight className="w-4 h-4" />,
      count: upcomingData?.count 
    },
    { id: 'calendar', label: 'Calendar', icon: <Calendar className="w-4 h-4" /> },
    { id: 'templates', label: 'Templates', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'learning', label: 'Learning', icon: <Brain className="w-4 h-4" /> },
  ];
  
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-amber-400" />
                Context Intelligence
              </h1>
              <p className="text-sm text-gray-400 mt-1">
                Teach PIQ about events that affect your business performance
              </p>
            </div>
            
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Event
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex items-center gap-1 mt-4 -mb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-amber-500 text-amber-400 bg-gray-800/50'
                    : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/30'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                    activeTab === tab.id ? 'bg-amber-500/20' : 'bg-gray-700'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Active Now Tab */}
        {activeTab === 'active' && (
          <div>
            {/* Summary Cards */}
            {activeData?.summary && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-white">{activeData.summary.total}</div>
                  <div className="text-sm text-gray-400">Active Events</div>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <div className="text-2xl font-bold text-emerald-400 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    {activeData.summary.expecting_up}
                  </div>
                  <div className="text-sm text-emerald-400/70">Expecting Growth</div>
                </div>
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4">
                  <div className="text-2xl font-bold text-rose-400 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" />
                    {activeData.summary.expecting_down}
                  </div>
                  <div className="text-sm text-rose-400/70">Expecting Decline</div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="text-2xl font-bold text-amber-400 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {activeData.summary.high_confidence}
                  </div>
                  <div className="text-sm text-amber-400/70">High Confidence</div>
                </div>
              </div>
            )}
            
            {/* Event List */}
            {activeLoading ? (
              <div className="text-center py-12 text-gray-400">Loading active events...</div>
            ) : activeData?.events.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-400">No Active Events</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Add events to help PIQ understand what's affecting your business
                </p>
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-4 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors"
                >
                  Add Your First Event
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {activeData?.events.map(event => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Upcoming Tab */}
        {activeTab === 'upcoming' && (
          <div>
            {upcomingLoading ? (
              <div className="text-center py-12 text-gray-400">Loading upcoming events...</div>
            ) : upcomingData?.events.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-400">No Upcoming Events</h3>
                <p className="text-sm text-gray-500 mt-1">
                  No events scheduled in the next {upcomingData?.look_ahead_days || 30} days
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingData?.events.map(event => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <ContextCalendar 
            events={allEventsData?.events || []} 
            onEventClick={handleEdit}
          />
        )}
        
        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <TemplateLibrary onAddFromTemplate={() => {
            setShowForm(true);
            queryClient.invalidateQueries({ queryKey: ['context-events'] });
          }} />
        )}
        
        {/* Learning Tab */}
        {activeTab === 'learning' && (
          <ContextLearning />
        )}
      </div>
      
      {/* Event Form Modal */}
      {showForm && (
        <ContextEventForm 
          event={editingEvent}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

export default ContextIntelligence;
